import { inject, Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { API_URL } from '../injection-tokens/api-url.token';
import { REPORT_URL } from '../injection-tokens/report-url.token';
import { HUB_URL } from '../injection-tokens/hub-url.token';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private apiUrl = inject(API_URL);
  private reportUrl = inject(REPORT_URL);
  private hubUrl = inject(HUB_URL);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isApiRequest = request.url.startsWith(this.apiUrl);
    const isReportRequest = request.url.startsWith(this.reportUrl);
    const isHubRequest = request.url.startsWith(this.hubUrl);
    const isProtectedRequest = isApiRequest || isReportRequest || isHubRequest;
    const isRefreshRequest = request.url.includes('/users/refresh-token');

    if (!isProtectedRequest || isRefreshRequest) {
      return next.handle(request).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.authService.logout();
          }
          return throwError(() => error);
        })
      );
    }

    const token = this.getTokenFromLocalStorage();

    const authRequest = token
      ? request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        })
      : request;

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.authService.logout();
        }
        return throwError(() => error);
      })
    );
  }

  private getTokenFromLocalStorage(): string | null {
    const token = localStorage.getItem('token');
    if (token) {
      return token;
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.endsWith('-auth')) {
        continue;
      }

      try {
        const raw = localStorage.getItem(key);
        if (!raw) {
          continue;
        }

        const parsed = JSON.parse(raw) as { token?: string };
        if (parsed?.token) {
          return parsed.token;
        }
      } catch {
      }
    }

    return null;
  }
}